<script setup lang="ts">
import { ref, watch, Ref, toRef } from 'vue';
import { CustomerMgmtTypes, useEditUser } from '@ilteducation/customer-mgmt';
import useQrCode from '../../composables/useQrCode';
import { usePrintLoginCard, AppNames } from '../../composables/useDownloadLoginCard';
import { generateQrToken, generateRandomPassword } from '../../utils';
import LoginCard from './LoginCard.vue';
import { useLocale } from '../../composables/useLocale';
import PermissionLock from '../PermissionLock.vue';
import { computed } from 'vue';

const { t } = useLocale();
const { createLoginLink, createQrCodeAsSVGDataURL } = useQrCode();
const { updateUser } = useEditUser();

type Props = {
  user: CustomerMgmtTypes.GetUserResponse;
  apps?: AppNames[];
  appUrl?: string;
  qrTokenDisabled?: string | false;
  isPasswordDisabled?: string | false;
  createPasswordCheck?: boolean;
  createQrCodeCheck?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  apps: () => ['ilteducation'],
  appUrl: '',
  createQrCodeCheck: false,
  createPasswordCheck: false,
});

const { appLogos, download, fullAppUrl, iltIcon } = usePrintLoginCard(props.apps, props.appUrl);

const loginLink = ref('');

const message = ref('');
const error = ref(false);
const isLoading = ref(false);

const createPassword = toRef(props.createPasswordCheck);
const createQrCode = toRef(props.createQrCodeCheck);

const userUpdated = ref(false);
const updateData: Ref<{
  password?: string;
  qrToken?: string;
}> = ref({
  password: '',
  qrToken: '',
});

const qrCodeImageData = ref('');

const updateUserLoginInfo = async () => {
  message.value = '';
  error.value = false;
  isLoading.value = true;

  if (!createPassword.value && !createQrCode.value) {
    message.value = t('lcp.select_at_least_one_option');
    error.value = true;
    isLoading.value = false;
    return false;
  }

  updateData.value = {
    password: createPassword.value ? generateRandomPassword() : undefined,
    qrToken: createQrCode.value ? generateQrToken() : undefined,
  };

  if (updateData.value.qrToken) {
    loginLink.value = createLoginLink(fullAppUrl.value, props.user.uid, updateData.value.qrToken);
    qrCodeImageData.value = await createQrCodeAsSVGDataURL(loginLink.value, iltIcon);
  }

  // set password and qr token for user
  const success = await updateUser(props.user.uid, updateData.value).catch((e) => {
    message.value = e.message;
    error.value = true;
  });
  if (!success) {
    isLoading.value = false;
    error.value = true;
    message.value = t('lcp.failed_to_create_login_card');
    return false;
  }

  userUpdated.value = true;
  isLoading.value = false;
  error.value = false;
  message.value = t('lcp.login_card_created_successfully');
  return true;
};

const downloadLoginCard = (): Promise<void> => {
  return download('login-card', props.user.userName);
};

const resetError = () => {
  error.value = false;
  message.value = '';
};

watch([createQrCode, createPassword], () => {
  resetError();
});

defineExpose({
  createLoginCardDisabled: computed(() => {
    return !createQrCode.value && !createPassword.value;
  }),
  updateUserLoginInfo,
  downloadLoginCard,
  error: error,
});
</script>

<template>
  <div>
    <div v-if="!userUpdated">
      <v-alert type="warning" variant="outlined" border="start">
        {{ t('lcp.create_login_card_warning') }}
      </v-alert>
      <p class="pt-6">{{ t('lcp.select_login_credentials') }}</p>
      <div class="d-flex align-center">
        <v-checkbox
          v-model="createQrCode"
          :disabled="!!qrTokenDisabled"
          :label="t('lcp.qrcode')"
          hide-details
        ></v-checkbox>
        <div>
          <PermissionLock v-if="qrTokenDisabled" :reason="qrTokenDisabled" />
        </div>
      </div>

      <div class="d-flex align-center">
        <v-checkbox
          v-model="createPassword"
          :disabled="!!isPasswordDisabled"
          :label="t('lcp.password')"
          hide-details
        ></v-checkbox>
        <div>
          <PermissionLock v-if="isPasswordDisabled" :reason="isPasswordDisabled" />
        </div>
      </div>

      <v-alert v-if="message" :type="error ? 'error' : 'success'" variant="outlined" border="start">
        {{ message }}
      </v-alert>
    </div>
    <div v-if="userUpdated">
      <div>
        <v-alert type="info" variant="outlined" border="start">
          {{ t('lcp.treat_login_card_as_sensitive') }}
        </v-alert>

        <p class="pt-6">
          {{ t('lcp.login_card_ready') }}
        </p>

        <div style="display: none">
          <v-btn variant="text" :href="loginLink" target="_blank">{{ t('lcp.login_as_user') }}</v-btn>
          <LoginCard
            id="login-card"
            ref="loginCard"
            v-if="userUpdated"
            :name="user.name"
            :user-name="user.userName"
            :app-logo-imgs="appLogos"
            :app-url="fullAppUrl"
            :password="updateData.password"
            :qr-code-image="qrCodeImageData"
            :qr-code-login-url="loginLink"
          />
        </div>
      </div>
    </div>
  </div>
</template>
